import { useEffect, useState, useRef, lazy, Suspense } from 'react';
import { X, Download, ExternalLink, Link, ClipboardCheck, MoreVertical, RefreshCw, AlertTriangle, FileText, ArrowLeft, ArrowRight } from 'lucide-react';
import { PdfViewer } from './PdfViewer';

const XlsxPreview = lazy(() => import('./XlsxPreview'));
const DocxPreview = lazy(() => import('./DocxPreview'));
const TextPreview = lazy(() => import('./TextPreview'));

interface FilePreviewProps {
  fileId: string;
  fileName: string;
  mimeType: string;
  spaceSlug?: string;
  onClose: () => void;
  onNext?: () => void;
  onPrev?: () => void;
}

const API_URL = '';

export function FilePreview({ fileId, fileName, mimeType, spaceSlug, onClose, onNext, onPrev }: FilePreviewProps) {
  const [resolvedFileName, setResolvedFileName] = useState(fileName);
  const [resolvedMimeType, setResolvedMimeType] = useState(mimeType);
  const [driveUrl, setDriveUrl] = useState<string>('');
  const [copyTooltip, setCopyTooltip] = useState(false);
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const previewActionsRef = useRef<HTMLDivElement>(null);



  // Content Loading / Error states
  const [contentLoading, setContentLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  // Synchronously reset loading state during render when fileId or retryKey changes
  // to avoid race conditions with cached image/iframe onLoad handlers.
  const [prevTrackedKey, setPrevTrackedKey] = useState(`${fileId}-${retryKey}`);
  const currentTrackedKey = `${fileId}-${retryKey}`;
  if (currentTrackedKey !== prevTrackedKey) {
    setPrevTrackedKey(currentTrackedKey);
    setContentLoading(true);
    setHasError(false);
  }



  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' && onNext) {
        onNext();
      } else if (e.key === 'ArrowLeft' && onPrev) {
        onPrev();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onNext, onPrev]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (previewActionsRef.current && !previewActionsRef.current.contains(event.target as Node)) {
        setActionsMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync props to state (useful when swiping to next/prev files)
  useEffect(() => {
    setResolvedFileName(fileName);
    setResolvedMimeType(mimeType);
    setDriveUrl('');
  }, [fileName, mimeType]);

  const querySuffix = spaceSlug ? `?spaceSlug=${encodeURIComponent(spaceSlug)}` : '';

  // 1. Fetch complete metadata on mount to retrieve the resolved driveUrl and file details
  useEffect(() => {
    let active = true;
    const infoUrl = `${API_URL}/api/files/${fileId}/info${querySuffix}`;
    fetch(infoUrl, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch file details');
        return res.json();
      })
      .then((data) => {
        if (!active) return;
        if (data.driveUrl) {
          setDriveUrl(data.driveUrl);
        }
        if (data.name) {
          setResolvedFileName(data.name);
        }
        if (data.mimeType) {
          setResolvedMimeType(data.mimeType);
        }
      })
      .catch((e) => {
        console.error('Failed to resolve file info:', e);
        if (active) {
          setHasError(true);
          setContentLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [fileId, querySuffix]);

  // 2. Log 'PREVIEW' activity on component mount or file ID change
  useEffect(() => {
    if (resolvedFileName === 'Loading...') return;
    const logUrl = `${API_URL}/api/files/${fileId}/log-activity${querySuffix}`;
    fetch(logUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ activityType: 'PREVIEW', fileName: resolvedFileName }),
      credentials: 'include',
    }).catch((err) => console.error('Failed to log preview:', err));
  }, [fileId, resolvedFileName, querySuffix]);

  const handleCopyLink = async () => {
    if (resolvedFileName === 'Loading...') return;
    const deepLinkUrl = window.location.href;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(deepLinkUrl);
      } else {
        // Fallback for non-secure contexts (e.g., HTTP subdomain local development)
        const textArea = document.createElement('textarea');
        textArea.value = deepLinkUrl;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (!successful) {
          throw new Error('execCommand copy fallback failed');
        }
      }

      setCopyTooltip(true);
      setTimeout(() => setCopyTooltip(false), 2000);

      // Log COPY_LINK activity
      const logUrl = `${API_URL}/api/files/${fileId}/log-activity${querySuffix}`;
      fetch(logUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ activityType: 'COPY_LINK', fileName: resolvedFileName }),
        credentials: 'include',
      }).catch((err) => console.error('Failed to log copy link:', err));
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleOpenInDrive = () => {
    if (resolvedFileName === 'Loading...') return;
    const url = driveUrl || `https://drive.google.com/open?id=${fileId}`;
    window.open(url, '_blank', 'noopener,noreferrer');

    // Log OPEN_IN_DRIVE activity
    const logUrl = `${API_URL}/api/files/${fileId}/log-activity${querySuffix}`;
    fetch(logUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ activityType: 'OPEN_IN_DRIVE', fileName: resolvedFileName }),
      credentials: 'include',
    }).catch((err) => console.error('Failed to log open in drive:', err));
  };

  const isMetadataLoading = resolvedFileName === 'Loading...' || !resolvedMimeType;
  const isPdf = resolvedMimeType.toLowerCase() === 'application/pdf';
  const isImage = resolvedMimeType.toLowerCase().startsWith('image/');
  const isXlsx = resolvedMimeType.toLowerCase() === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                 resolvedMimeType.toLowerCase() === 'application/vnd.google-apps.spreadsheet';
  const isDocx = resolvedMimeType.toLowerCase() === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                 resolvedMimeType.toLowerCase() === 'application/vnd.google-apps.document';
  const isText = resolvedMimeType.toLowerCase().startsWith('text/') ||
                 resolvedMimeType.toLowerCase() === 'application/json' ||
                 resolvedMimeType.toLowerCase().includes('javascript') ||
                 resolvedMimeType.toLowerCase().includes('typescript') ||
                 resolvedFileName.toLowerCase().match(/\.(txt|md|csv|json|js|jsx|ts|tsx|html|css|scss|xml|yaml|yml|py|sh|go)$/) !== null;
  const canPreview = isPdf || isImage || isXlsx || isDocx || isText;

  const isActuallyLoading = isMetadataLoading || (canPreview && contentLoading);

  const previewSeparator = querySuffix ? '&' : '?';
  const previewUrl = `/api/files/${fileId}/preview${querySuffix}`;
  const downloadUrl = `${API_URL}/api/files/${fileId}/download${querySuffix}`;

  const isMobile = typeof navigator !== 'undefined' && /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const absolutePreviewUrl = window.location.origin + previewUrl;
  const pdfIframeSrc = isPdf && isMobile
    ? `https://docs.google.com/viewer?url=${encodeURIComponent(absolutePreviewUrl)}&embedded=true`
    : `${previewUrl}${previewSeparator}t=${retryKey}`;

  const isActionsDisabled = resolvedFileName === 'Loading...';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="modal-content preview-modal-content animate-scale-in" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Custom Documents Actions Toolbar */}
        <div className="modal-header preview-modal-header">
          <h3 className="modal-title" title={resolvedFileName}>
            {resolvedFileName}
          </h3>
          <div className="modal-header-actions" ref={previewActionsRef}>
            {/* Desktop Actions Row */}
            <div className="preview-desktop-actions">
              <button
                className="btn btn-secondary"
                onClick={handleCopyLink}
                title="Copy Link"
                disabled={isActionsDisabled}
              >
                {copyTooltip ? <ClipboardCheck size={14} /> : <Link size={14} />}
                {copyTooltip ? 'Copied!' : 'Copy Link'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleOpenInDrive}
                title="Open in Google Drive"
                disabled={isActionsDisabled}
              >
                <ExternalLink size={14} /> Open in Drive
              </button>
              <a
                href={isActionsDisabled ? '#' : downloadUrl}
                onClick={(e) => { if (isActionsDisabled) e.preventDefault(); }}
                className={`btn btn-primary ${isActionsDisabled ? 'disabled' : ''}`}
                style={{ textDecoration: 'none' }}
                title="Download Document"
              >
                <Download size={14} /> Download
              </a>
            </div>

            {/* Mobile Actions Dropdown */}
            <div className="preview-mobile-actions">
              <button 
                className="icon-btn actions-menu-btn" 
                onClick={() => setActionsMenuOpen(!actionsMenuOpen)}
                title="More Actions"
                disabled={isActionsDisabled}
              >
                <MoreVertical size={16} />
              </button>
              {actionsMenuOpen && !isActionsDisabled && (
                <div className="preview-actions-dropdown">
                  <button
                    className="btn btn-secondary preview-action-item"
                    onClick={() => {
                      setActionsMenuOpen(false);
                      handleCopyLink();
                    }}
                  >
                    {copyTooltip ? <ClipboardCheck size={14} /> : <Link size={14} />}
                    {copyTooltip ? 'Copied!' : 'Copy Link'}
                  </button>
                  <button
                    className="btn btn-secondary preview-action-item"
                    onClick={() => {
                      setActionsMenuOpen(false);
                      handleOpenInDrive();
                    }}
                  >
                    <ExternalLink size={14} /> Open in Drive
                  </button>
                  <a
                    href={downloadUrl}
                    className="btn btn-primary preview-action-item"
                    style={{ textDecoration: 'none' }}
                    onClick={() => setActionsMenuOpen(false)}
                  >
                    <Download size={14} /> Download
                  </a>
                </div>
              )}
            </div>

            {/* Close button - always visible, pinned */}
            <button
              className="modal-close"
              onClick={onClose}
              aria-label="Close preview"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="modal-body">
          {/* Spinner for either Metadata loading or Content loading */}
          {isActuallyLoading && !hasError && (
            <div className="preview-loading">
              <div className="loading-spinner"></div>
              <p>Loading preview...</p>
            </div>
          )}

          {hasError ? (
            <div className="fallback-preview">
              <div className="fallback-icon" style={{ display: 'flex', justifyContent: 'center', color: 'var(--text-muted)' }}>
                <AlertTriangle size={48} />
              </div>
              <h3>Preview Load Failed</h3>
              <p style={{ color: 'var(--text-secondary)', maxWidth: '400px', fontSize: '0.9rem' }}>
                An error occurred while attempting to preview this file. Please check your internet connection or try again.
              </p>
              <button 
                className="btn btn-primary"
                onClick={() => setRetryKey(prev => prev + 1)}
              >
                <RefreshCw size={14} /> Retry Preview
              </button>
            </div>
          ) : (
            !isMetadataLoading && (
              canPreview ? (
                isImage ? (
                  <img
                    src={`${previewUrl}${previewSeparator}t=${retryKey}`}
                    alt={resolvedFileName}
                    onLoad={() => setContentLoading(false)}
                    onError={() => {
                      setHasError(true);
                      setContentLoading(false);
                    }}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      borderRadius: '4px',
                      opacity: contentLoading ? 0 : 1,
                      transition: 'opacity 0.25s ease-in-out',
                      display: contentLoading ? 'none' : 'block'
                    }}
                  />
                ) : isPdf && isMobile ? (
                  <PdfViewer
                    url={`${previewUrl}${previewSeparator}t=${retryKey}`}
                    onLoadSuccess={() => setContentLoading(false)}
                    onLoadError={() => {
                      setHasError(true);
                      setContentLoading(false);
                    }}
                  />
                ) : isPdf ? (
                  <iframe
                    src={pdfIframeSrc}
                    title={resolvedFileName}
                    className="preview-iframe"
                    onLoad={() => setContentLoading(false)}
                    onError={() => {
                      setHasError(true);
                      setContentLoading(false);
                    }}
                    style={{
                      opacity: contentLoading ? 0 : 1,
                      transition: 'opacity 0.25s ease-in-out'
                    }}
                  />
                ) : isXlsx ? (
                  <Suspense fallback={null}>
                    <XlsxPreview
                      url={`${previewUrl}${previewSeparator}t=${retryKey}`}
                      onLoadSuccess={() => setContentLoading(false)}
                      onLoadError={() => {
                        setHasError(true);
                        setContentLoading(false);
                      }}
                    />
                  </Suspense>
                ) : isDocx ? (
                  <Suspense fallback={null}>
                    <DocxPreview
                      url={`${previewUrl}${previewSeparator}t=${retryKey}`}
                      onLoadSuccess={() => setContentLoading(false)}
                      onLoadError={() => {
                        setHasError(true);
                        setContentLoading(false);
                      }}
                    />
                  </Suspense>
                ) : isText ? (
                  <Suspense fallback={null}>
                    <TextPreview
                      url={`${previewUrl}${previewSeparator}t=${retryKey}`}
                      onLoadSuccess={() => setContentLoading(false)}
                      onLoadError={() => {
                        setHasError(true);
                        setContentLoading(false);
                      }}
                    />
                  </Suspense>
                ) : null
              ) : (
                <div className="fallback-preview">
                  <div className="fallback-icon" style={{ display: 'flex', justifyContent: 'center', color: 'var(--text-muted)' }}>
                    <FileText size={48} />
                  </div>
                  <h3>Preview Not Available</h3>
                  <p style={{ color: 'var(--text-secondary)', maxWidth: '400px', fontSize: '0.9rem' }}>
                    Previews for this file type ({resolvedMimeType}) are not supported in the MVP.
                    Please download the file to view it on your local device.
                  </p>
                  
                  <a 
                    href={downloadUrl} 
                    className="btn btn-primary"
                    style={{ textDecoration: 'none' }}
                  >
                    <Download size={14} /> Download Document
                  </a>
                </div>
              )
            )
          )}
        </div>

        <div className="preview-nav-hint">
          <button 
            className="nav-btn" 
            onClick={onPrev} 
            disabled={!onPrev}
            title="Previous file"
          >
            <ArrowLeft size={14} /> Prev
          </button>
          <span>Use Arrow keys to navigate</span>
          <button 
            className="nav-btn" 
            onClick={onNext} 
            disabled={!onNext}
            title="Next file"
          >
            Next <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
